// AI-CONTEXT: See docs/features/impact-in-beeld-fase2.md

/**
 * Timestamp-of-ms → ms. Accepteert Firestore Timestamps (toMillis) én kale
 * epoch-ms, zodat callers niet per ongeluk appels met peren vergelijken.
 * AI-CONTEXT: See docs/pitfalls.md#api-callable-timestamp-vs-ms
 *
 * @param {number|{toMillis: () => number}|null|undefined} value
 * @returns {number|null}
 */
const toMs = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (value && typeof value.toMillis === 'function') return value.toMillis()
    return null
}

/**
 * Bepaalt of een gepubliceerde uiting verouderd is ("Actie nodig"), volgens
 * plan-besluit 3: prijs-/productwijziging ná publicatie of kengetal-drift.
 *
 * @param {object} input
 * @param {number|{toMillis: () => number}|null} input.publishedAtMs - Publicatiemoment (null = nooit gepubliceerd)
 * @param {number|null} input.publishedKengetal - Kengetal in de gepubliceerde snapshot
 * @param {number|null} input.currentKengetal - Actueel kengetal (null = nog aan het laden → geen oordeel)
 * @param {Array<{ID: string, ModifiedAt?: number|{toMillis: () => number}}>} input.shownProducts - Producten die nu zichtbaar zijn (centrale hook, soft-deleted al weggefilterd)
 * @param {Array<string>} input.shownProductIds - Product-IDs die de publicatie toont
 * @returns {{actionNeeded: boolean, reasons: Array<'product-changed'|'product-missing'|'kengetal-changed'>}}
 */
const deriveActionNeeded = ({
    publishedAtMs,
    publishedKengetal,
    currentKengetal,
    shownProducts,
    shownProductIds,
} = {}) => {
    const publishedMs = toMs(publishedAtMs)
    if (publishedMs === null) return { actionNeeded: false, reasons: [] }

    const reasons = []
    const products = Array.isArray(shownProducts) ? shownProducts : []
    const ids = Array.isArray(shownProductIds) ? shownProductIds : []

    ids.forEach((id) => {
        const product = products.find((candidate) => candidate.ID === id)

        if (!product) {
            // Niet meer in de (soft-delete-gefilterde) lijst: verwijderd na publicatie.
            if (!reasons.includes('product-missing')) reasons.push('product-missing')
            return
        }

        const modifiedMs = toMs(product.ModifiedAt)
        if (modifiedMs !== null && modifiedMs > publishedMs) {
            if (!reasons.includes('product-changed')) reasons.push('product-changed')
        }
    })

    // Null/undefined aan één van beide kanten = nog aan het laden — dan géén
    // vals alarm. (Een gepubliceerde snapshot heeft altijd een Kengetal;
    // publiceren blokkeert zonder presenteerbaar kengetal.)
    if (currentKengetal !== null && currentKengetal !== undefined
        && publishedKengetal !== null && publishedKengetal !== undefined
        && currentKengetal !== publishedKengetal) {
        reasons.push('kengetal-changed')
    }

    return { actionNeeded: reasons.length > 0, reasons }
}

export { deriveActionNeeded }
